/**
 * The conflict map for a company roll-up (roadmap P-2).
 *
 * A roll-up merges several organizations' published lists into one scenario,
 * in source order (`applyPortfolioRollupSources`). Where two organizations
 * list the same item, the merge keeps one rank and one owner and drops the
 * other without saying so. This finds every such item before the merge runs:
 * which lists hold it, at what position in each, and which list the merge
 * will follow. Items are matched the way the merge matches them — by title,
 * ignoring case and surrounding spaces.
 *
 * Pure. Lists come from `resolvePortfolioRollupLists`.
 */

export interface RollupList {
  label: string;
  owningOrg?: string;
  items: { id: string; title: string }[];
}

export interface RollupClaim {
  listLabel: string;
  owningOrg?: string;
  /** 1-based position in that organization's own list. */
  position: number;
}

export interface RollupConflict {
  title: string;
  claims: RollupClaim[];
  /** The claim the merge keeps — the last list that holds the item. */
  kept: RollupClaim;
  /** Largest gap between the positions the organizations gave it. */
  positionSpread: number;
  /** True when the lists name different owning organizations (unnamed owners never count). */
  differentOwners: boolean;
}

export function findRollupConflicts(lists: RollupList[]): RollupConflict[] {
  const byKey = new Map<string, { title: string; claims: RollupClaim[] }>();
  for (const list of lists) {
    const seenInList = new Set<string>();
    list.items.forEach((item, i) => {
      const key = item.title.trim().toLowerCase();
      if (seenInList.has(key)) return;
      seenInList.add(key);
      const entry = byKey.get(key) ?? { title: item.title.trim(), claims: [] };
      entry.claims.push({ listLabel: list.label, owningOrg: list.owningOrg, position: i + 1 });
      byKey.set(key, entry);
    });
  }

  const conflicts: RollupConflict[] = [];
  for (const { title, claims } of byKey.values()) {
    if (claims.length < 2) continue;
    const positions = claims.map((c) => c.position);
    const owners = new Set(claims.map((c) => c.owningOrg).filter(Boolean));
    conflicts.push({
      title,
      claims,
      kept: claims[claims.length - 1],
      positionSpread: Math.max(...positions) - Math.min(...positions),
      differentOwners: owners.size > 1,
    });
  }
  conflicts.sort((a, b) => b.positionSpread - a.positionSpread || a.title.localeCompare(b.title));
  return conflicts;
}
